import { useTranslation } from 'react-i18next';
import { Box, useTheme, useMediaQuery, Stack } from '@mui/material';
import {
  HeadMetaData,
  AnimatedVideoBackground,
  SongActionButtonsRow,
} from 'components';
import Checkout from 'components/Checkout';
import { Song } from 'services/store/songs/types';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import { useAddToLibrary, useRemoveFromLibrary } from 'services/hooks';
import { toColonTimeFromMs } from 'services/helpers/time';
import { locations } from 'services/routes/locations';
import { SongInfo, Player, MobileSongActionButtonsRow } from './components';
import {
  StyledHeadline,
  StyledPageContainer,
  StyledSongActionButtonsRowContainer,
  StyledSongDetailsContainer,
  StyledSongDetailsWrapper,
} from './styled';
import InfoBanner from './components/InfoBanner/InfoBanner';

type PropsType = {
  song: Song;
};

const SongDetails = ({ song }: PropsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const location = AnalyticsEventLocation.SONG_DETAILS;
  const { removeSongFromLibrary } = useRemoveFromLibrary({
    songId: song.id,
    location,
  });
  const { addSongToLibrary } = useAddToLibrary({
    songId: song.id,
    location,
  });

  // @ts-ignore
  const { genres, title, duration } = song || {};
  const genreName = genres?.[0]?.name ? `| ${genres?.[0]?.name}` : '';
  const pageTitle = `${title} ${genreName} | ${t('meta:songDetailsTitle')}`;

  return (
    <StyledPageContainer data-testid="songDetail">
      <HeadMetaData
        title={pageTitle}
        contentDescription={t('meta:songDetailsContentDescription', {
          songTitle: title,
        })}
      />

      <AnimatedVideoBackground>
        <StyledSongDetailsWrapper>
          <StyledSongDetailsContainer>
            <Stack spacing={theme.spacing(2)}>
              <StyledHeadline component="h1">{song.title}</StyledHeadline>
              <Box>{toColonTimeFromMs(duration)}</Box>
              <Box
                sx={{ py: isMobile ? theme.spacing(0.5) : theme.spacing(3) }}
              >
                {/* @ts-ignore */}
                <Player isPlayIconEnlarged song={song} peak={song.peaks} />
              </Box>
              <StyledSongActionButtonsRowContainer>
                {!isMobile && (
                  <SongActionButtonsRow
                    song={song}
                    location={location}
                    removeFromLibrary={removeSongFromLibrary}
                    addToLibrary={addSongToLibrary}
                  />
                )}
                <SongInfo song={song} />
              </StyledSongActionButtonsRowContainer>
              {!isMobile && (
                <InfoBanner url={locations.general.aiMusicGenerator.pathname} />
              )}
            </Stack>
          </StyledSongDetailsContainer>

          {isMobile && (
            <MobileSongActionButtonsRow
              song={song}
              location={location}
              removeFromLibrary={removeSongFromLibrary}
              addToLibrary={addSongToLibrary}
            />
          )}
          {isMobile && (
            <InfoBanner url={locations.general.aiMusicGenerator.pathname} />
          )}
        </StyledSongDetailsWrapper>
      </AnimatedVideoBackground>
      <Checkout shouldBlockRedirect />
    </StyledPageContainer>
  );
};

export default SongDetails;
